import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  addOrderToDispatch,
  removeOrderToDispatch,
  selectMapHiddenOrdersCount,
  selectMapVisibleOrders,
} from "../../store/actions/ordersSlice";
import { MapCenterUpdater } from "../../utils/map";
import ColoredGrid from "./ColoredGrid";
import MapOrderLimitBanner from "./MapOrderLimitBanner";
import SmartRoutingOverlay from "./SmartRoutingOverlay";

// Single Responsibility: Custom icon creation with map marker and text
const createCustomIcon = (text, color = "#FF6B35", isHovered = false) => {
  const markerWidth = 60;
  const markerHeight = 80;

  const getHoverColor = (baseColor) => {
    const r = parseInt(baseColor.slice(1, 3), 16);
    const g = parseInt(baseColor.slice(3, 5), 16);
    const b = parseInt(baseColor.slice(5, 7), 16);

    const brightenFactor = 0.2;
    const newR = Math.min(255, Math.floor(r * (1 + brightenFactor)));
    const newG = Math.min(255, Math.floor(g * (1 + brightenFactor)));
    const newB = Math.min(255, Math.floor(b * (1 + brightenFactor)));

    const rHex = newR.toString(16).padStart(2, "0");
    const gHex = newG.toString(16).padStart(2, "0");
    const bHex = newB.toString(16).padStart(2, "0");

    return `#${rHex}${gHex}${bHex}`;
  };

  const getDarkerColor = (baseColor, factor = 0.3) => {
    const r = parseInt(baseColor.slice(1, 3), 16);
    const g = parseInt(baseColor.slice(3, 5), 16);
    const b = parseInt(baseColor.slice(5, 7), 16);

    const newR = Math.max(0, Math.floor(r * (1 - factor)));
    const newG = Math.max(0, Math.floor(g * (1 - factor)));
    const newB = Math.max(0, Math.floor(b * (1 - factor)));

    const rHex = newR.toString(16).padStart(2, "0");
    const gHex = newG.toString(16).padStart(2, "0");
    const bHex = newB.toString(16).padStart(2, "0");

    return `#${rHex}${gHex}${bHex}`;
  };

  const displayColor = isHovered ? getHoverColor(color) : color;
  const darkerColor = getDarkerColor(displayColor);
  const shadowBlur = isHovered
    ? "0 6px 12px rgba(0, 0, 0, 0.35)"
    : "0 4px 8px rgba(0, 0, 0, 0.25)";
  const transition = "filter 0.2s ease, fill 0.2s ease, transform 0.2s ease";

  const customIcon = L.divIcon({
    className: "custom-icon",
    html: `
      <div style="
        position: relative;
        display: flex;
        justify-content: center;
        width: ${markerWidth}px;
        height: ${markerHeight}px;
        z-index: ${isHovered ? 1000 : "auto"};
        transform: ${isHovered ? "translateY(-2px)" : "translateY(0)"};
        transition: ${transition};
      ">
        <svg 
          viewBox="0 0 24 36" 
          width="${markerWidth}px" 
          height="${markerHeight}px" 
          style="
            filter: drop-shadow(${shadowBlur});
            position: absolute;
            z-index: ${isHovered ? 1000 : 1};
            transition: ${transition};
          "
        >
          <defs>
            <linearGradient id="markerGradient-${text}-${color}" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style="stop-color:${displayColor};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${darkerColor};stop-opacity:1" />
            </linearGradient>
            
            <radialGradient id="whiteCircleGradient-${text}" cx="30%" cy="30%">
              <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#f0f0f0;stop-opacity:1" />
            </radialGradient>
          </defs>
          
          <path 
            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" 
            fill="url(#markerGradient-${text}-${color})"
          />
          
          <circle 
            cx="12" 
            cy="9" 
            r="6" 
            fill="url(#whiteCircleGradient-${text})"
            filter="url(#innerShadow-${text})"
          />
          
          <ellipse 
            cx="12" 
            cy="7.5" 
            rx="3" 
            ry="1.5" 
            fill="white" 
            opacity="0.4"
          />
        </svg>
        <div style="
          position: absolute;
          top: 11%;
          left: 0;
          right: 0;
          font-weight: bold;
          font-size: 15px;
          font-family: Arial, sans-serif;
          color: #1a1a1a;
          text-align: center;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          pointer-events: none;
          z-index: ${isHovered ? 1001 : 2};
          text-shadow: 0 1px 2px rgba(255,255,255,0.8);
        ">
          ${text}
        </div>
      </div>
    `,
    iconSize: [markerWidth, markerHeight],
    iconAnchor: [markerWidth / 2, markerHeight - 30],
    popupAnchor: [0, -markerHeight / 2],
  });

  return customIcon;
};

// Create store icon (keeping this separate since it's different from order markers)
const createStoreIcon = (isHovered = false) => {
  const size = 36; // Fixed size, no scaling on hover

  // Slightly brighter blue on hover
  const storeColor = isHovered ? "#50A9F7" : "#2196F3";

  const shadowBlur = isHovered
    ? "0 3px 6px rgba(0,0,0,0.4)"
    : "0 2px 5px rgba(0,0,0,0.3)";

  const transition = "background-color 0.2s ease, box-shadow 0.2s ease";

  const storeIcon = L.divIcon({
    className: "custom-icon",
    html: `
      <div style="
        background-color: ${storeColor};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        color: white;
        box-shadow: ${shadowBlur};
        transition: ${transition};
        z-index: ${isHovered ? 1000 : "auto"};
      ">
        <svg viewBox="0 0 24 24" width="${size * 0.55}" height="${size * 0.55}">
          <path d="M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z"></path>
        </svg>
      </div>
    `,
    iconSize: [size, size],
    // For the store icon (circle), the anchor should be center of the circle
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });

  return storeIcon;
};

// Single Responsibility: Map view controller
const MapViewController = ({ center, sidebarOpen }) => {
  const map = useMap();

  useEffect(() => {
    if (center && center.length === 2) {
      const currentZoom = map.getZoom();

      if (sidebarOpen) {
        // Calculate offset to center the target in the remaining 60% of screen
        // Available space is left 60% (if sidebar is 40%). Center of available is 30%.
        // Screen center is 50%. Difference is 20%.
        // To move a point from 50% to 30% (left), we move the camera to the RIGHT.
        const mapSize = map.getSize();
        // Shift by 20% of width
        const offsetX = mapSize.x * 0.20;

        // Project current center to pixels using current zoom
        const centerPoint = map.project(center, currentZoom);

        // Add positive offset to Move Camera Right -> Features Move Left
        const newCenterPoint = centerPoint.add([offsetX, 0]);

        const newCenter = map.unproject(newCenterPoint, currentZoom);
        map.panTo(newCenter, { animate: true, duration: 0.5 });
      } else {
        map.panTo(center, { animate: true, duration: 0.5 });
      }
    }
  }, [center, map, sidebarOpen]);

  return null;
};

// Custom Leaflet marker that handles clicks and hover - optimized with React.memo
const CustomMarker = React.memo(
  ({ position, icon, order, onClick, children }) => {
    const markerRef = React.useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    // Determine marker color based on requirements
    const getMarkerColor = useCallback((order, ordersToDispatch) => {
      // If in ordersToDispatch, use yellow
      if (ordersToDispatch.includes(order.order_id)) {
        return "#FFEB3B"; // Yellow
      }
      // Otherwise, color based on status_number
      else if (order.status_number === 2) {
        return "#FF6B35"; // Orange
      } else if (order.status_number === 7) {
        return "#2196F3"; // Blue
      }
    }, []);

    // Get ordersToDispatch from Redux store
    const ordersToDispatch = useSelector(
      (state) => state.orders.ordersToDispatch || []
    );

    // Dynamically create the icon based on hover state
    const dynamicIcon = useMemo(() => {
      const color = getMarkerColor(order, ordersToDispatch);
      return createCustomIcon((order.order_index ?? "").toString(), color, isHovered);
    }, [order, ordersToDispatch, isHovered, getMarkerColor]);

    // Memoize event handlers to prevent unnecessary re-renders
    const eventHandlers = useMemo(
      () => ({
        click: () => {
          onClick(order);
        },
        mouseover: () => {
          setIsHovered(true);
          // When hovering, bring the marker to front
          if (markerRef.current) {
            markerRef.current.getElement().style.zIndex = 1000;
          }
        },
        mouseout: () => {
          setIsHovered(false);
          // Reset z-index when not hovering
          if (markerRef.current) {
            markerRef.current.getElement().style.zIndex = "";
          }
        },
      }),
      [onClick, order]
    );

    return (
      <Marker
        position={position}
        icon={dynamicIcon}
        eventHandlers={eventHandlers}
        ref={markerRef}
        zIndexOffset={isHovered ? 1000 : 0}
      >
        {children}
      </Marker>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Only re-render if these props change
    return (
      prevProps.position[0] === nextProps.position[0] &&
      prevProps.position[1] === nextProps.position[1] &&
      prevProps.order.order_id === nextProps.order.order_id &&
      prevProps.order.status_number === nextProps.order.status_number
    );
  }
);

// Ensure display name is set for debugging
CustomMarker.displayName = "CustomMarker";

// Store marker component with hover effects
const StoreMarker = React.memo(({ position, title }) => {
  const [isHovered, setIsHovered] = useState(false);
  const markerRef = React.useRef(null);

  const storeIcon = useMemo(() => createStoreIcon(isHovered), [isHovered]);

  const eventHandlers = useMemo(
    () => ({
      mouseover: () => {
        setIsHovered(true);
        if (markerRef.current) {
          markerRef.current.getElement().style.zIndex = 1000;
        }
      },
      mouseout: () => {
        setIsHovered(false);
        if (markerRef.current) {
          markerRef.current.getElement().style.zIndex = "";
        }
      },
    }),
    []
  );

  return (
    <Marker
      position={position}
      icon={storeIcon}
      eventHandlers={eventHandlers}
      ref={markerRef}
      zIndexOffset={isHovered ? 1000 : 0}
    >
      <Popup>
        <Typography variant="body1">{title}</Typography>
      </Popup>
    </Marker>
  );
});

StoreMarker.displayName = "StoreMarker";

// Main component
const OrderMap = ({ sidebarOpen }) => {
  const dispatch = useDispatch();

  // Redux selectors (using camelCase as per preference)
  const mapCenter = useSelector((state) => state.map.center);
  const deliveryCenterLat = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.lat || 0
  );
  const deliveryCenterLng = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.lng || 0
  );
  // Already filtered to mappable orders and capped by the plan's map limit.
  const orders = useSelector(selectMapVisibleOrders);
  const hiddenOrdersCount = useSelector(selectMapHiddenOrdersCount);
  const mapOrderLimit = useSelector((state) => state.map.orderLimit);
  const ordersToDispatch = useSelector(
    (state) => state.orders.ordersToDispatch || []
  );
  const enablePackedControl = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.config.enable_packed_control
  );

  // Handle marker click - memoized to prevent unnecessary re-renders
  const handleMarkerClick = useCallback(
    async (order) => {
      try {
        if (order.status_number <= 2 && enablePackedControl) {
          return false;
        }

        if (ordersToDispatch.includes(order.order_id)) {
          return dispatch(removeOrderToDispatch(order.order_id));
        }

        dispatch(addOrderToDispatch(order.order_id));
      } catch (e) {
        console.error(e);
      }
    },
    [dispatch, ordersToDispatch, enablePackedControl]
  );

  // Compute default center if mapCenter is not available (memoized)
  const defaultCenter = useMemo(() => {
    // Convert mapCenter object {lat, lng} to array [lat, lng] for Leaflet
    if (mapCenter && mapCenter.lat && mapCenter.lng) {
      return [mapCenter.lat, mapCenter.lng];
    }
    return [deliveryCenterLat, deliveryCenterLng] || [0, 0];
  }, [mapCenter, deliveryCenterLat, deliveryCenterLng]);

  // Check if we have valid coordinates - memoized function
  const isValidCoordinate = useCallback((lat, lng) => {
    return lat !== 0 && lng !== 0 && lat !== null && lng !== null;
  }, []);

  // Memoize the filtered and mapped markers to prevent unnecessary recalculations
  const orderMarkers = useMemo(() => {
    if (!orders) return [];

    return orders.map((order) => (
      <CustomMarker
        key={order.order_id}
        position={[order.LAT, order.LONG]}
        order={order}
        onClick={handleMarkerClick}
      />
    ));
  }, [orders, handleMarkerClick]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        minHeight: "500px",
        position: "relative",
      }}
    >
      <MapContainer
        center={defaultCenter}
        zoom={13}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Map view controller for dynamic center changes */}
        <MapViewController
          center={defaultCenter}
          sidebarOpen={sidebarOpen}
        />

        {/* Delivery Center Marker */}
        {isValidCoordinate(deliveryCenterLat, deliveryCenterLng) && (
          <StoreMarker
            position={[deliveryCenterLat, deliveryCenterLng]}
            title="Delivery Center"
          />
        )}

        {/* Order Markers - now memoized */}
        {orderMarkers}

        {/* Add the 2x2 colored grid centered at delivery center */}
        <ColoredGrid />

        {/* Smart routing polylines + stop markers (only when "Rotas" tab is active) */}
        <SmartRoutingOverlay />
      </MapContainer>

      {/* Plan cap notice — sits above the map, outside MapContainer */}
      <MapOrderLimitBanner
        hiddenCount={hiddenOrdersCount}
        limit={mapOrderLimit}
      />
    </Box>
  );
};

export default React.memo(OrderMap);
